'use client'
import React from 'react'
import { Box, Button } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { MultiCitySegment } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import { TravelersInput } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/TravelersInput'
import { TranslationFunction } from '@/src/types/translations'

import { MultiCitySegmentWrapper } from './components/MultiCitySegmentWrapper'

interface DesktopMultiCitySearchFormProps {
    segments: MultiCitySegment[]
    segmentsWithSameAirportError: Set<number>
    travelersState: { adults: number; childs: number; infants: number }
    flightClass: string
    canAddMoreSegment: boolean
    showRemoveSegment: boolean
    onSegmentFieldActivation: (segmentIndex: number, field: 'from' | 'to' | 'date') => void
    onAirportSelect: (segmentIndex: number, value: string, action: 'from' | 'to') => void
    onDateSelect: (segmentIndex: number, dates: any, range: any) => void
    onSwap: (segmentIndex: number) => void
    onAddMore: () => void
    onRemove: (segmentIndex: number) => void
    onSearch: () => void
    getAirportsForSegment: (query: string, segmentIndex: number, field: 'from' | 'to') => void
    isSegmentFieldActive: (segmentIndex: number, field: 'from' | 'to' | 'date') => boolean
    isComponentActive: (component: 'from' | 'to' | 'date' | 'travelers' | null) => boolean
    handleComponentActivation: (component: 'from' | 'to' | 'date' | 'travelers' | null) => void
    closeActiveComponent: () => void
    closeActiveSegmentComponent: () => void
    handleSegment0ClickAway: (event: Event | React.SyntheticEvent) => void
    createSegmentClickAwayHandler: (segmentIndex: number) => (event: Event | React.SyntheticEvent) => void
    getMinDateForSegment: (segmentIndex: number) => Date | null
    handleTravelerChange: (type: 'adults' | 'childs' | 'infants', delta: number) => void
    handleFlightClassChange: (newClass: string) => void
    canIncreaseAdults: boolean
    canIncreaseChildren: boolean
    canIncreaseInfants: boolean
    canDecreaseAdults: boolean
    canDecreaseChildren: boolean
    canDecreaseInfants: boolean
    t: TranslationFunction<'common'>
}

export const DesktopMultiCitySearchForm: React.FC<DesktopMultiCitySearchFormProps> = ({
    segments,
    segmentsWithSameAirportError,
    travelersState,
    flightClass,
    canAddMoreSegment,
    onSegmentFieldActivation,
    onAirportSelect,
    onDateSelect,
    onSwap,
    onAddMore,
    onRemove,
    onSearch,
    getAirportsForSegment,
    isSegmentFieldActive,
    isComponentActive,
    handleComponentActivation,
    closeActiveComponent,
    closeActiveSegmentComponent,
    createSegmentClickAwayHandler,
    getMinDateForSegment,
    handleTravelerChange,
    handleFlightClassChange,
    canIncreaseAdults,
    canIncreaseChildren,
    canIncreaseInfants,
    canDecreaseAdults,
    canDecreaseChildren,
    canDecreaseInfants,
    t
}) => {
    return (
        <Box>
            {/* All segments rendered uniformly */}
            {segments.map((segment, index) => (
                <MultiCitySegmentWrapper
                    key={index}
                    segment={segment}
                    segmentIndex={index}
                    segmentsLength={segments.length}
                    segmentsWithSameAirportError={segmentsWithSameAirportError}
                    isSegmentFieldActive={isSegmentFieldActive}
                    onSegmentFieldActivation={onSegmentFieldActivation}
                    onAirportSelect={onAirportSelect}
                    onDateSelect={onDateSelect}
                    onSwap={onSwap}
                    onRemove={onRemove}
                    getAirportsForSegment={getAirportsForSegment}
                    closeActiveSegmentComponent={closeActiveSegmentComponent}
                    createSegmentClickAwayHandler={createSegmentClickAwayHandler}
                    getMinDateForSegment={getMinDateForSegment}
                />
            ))}

            {/* Bottom row: Add More (left) + Travelers + Search (right) */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                {canAddMoreSegment && (
                    <Button
                        onClick={onAddMore}
                        variant="outlined"
                        size="medium"
                        startIcon={<AddIcon />}
                        sx={{
                            py: 1,
                            px: 3,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.875rem'
                        }}
                    >
                        {t('addMore')}
                    </Button>
                )}
                {!canAddMoreSegment && <Box />}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        // onClick={() => handleComponentActivation('travelers')}
                        sx={{
                            border: 1,
                            borderColor: 'grey.300',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: 50,
                            px: 2,
                            cursor: 'pointer',
                            minWidth: 200,
                            maxWidth: 200,
                            overflow: 'hidden'
                        }}
                    >
                        <TravelersInput
                            adults={travelersState.adults}
                            childs={travelersState.childs}
                            infants={travelersState.infants}
                            flightClass={flightClass}
                            onTravelerChange={handleTravelerChange}
                            onFlightClassChange={handleFlightClassChange}
                            canIncreaseAdults={canIncreaseAdults}
                            canIncreaseChildren={canIncreaseChildren}
                            canIncreaseInfants={canIncreaseInfants}
                            canDecreaseAdults={canDecreaseAdults}
                            canDecreaseChildren={canDecreaseChildren}
                            canDecreaseInfants={canDecreaseInfants}
                            active={isComponentActive('travelers')}
                            onActivate={() => handleComponentActivation('travelers')}
                            onClose={closeActiveComponent}
                        />
                    </Box>
                    <Button
                        variant="customVariantPrimary"
                        sx={{
                            borderRadius: 2,
                            py: 1.5,
                            px: 3,
                            minWidth: 140,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                        onClick={onSearch}
                    >
                        {t('searchFlights')}
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
